import type {
  GetFilteredBalancesData,
  GetFilteredBalancesProps,
  GetFilteredTokensParams,
  GetVisibleTokenColumnsParams,
} from '../core/types';

export const getFilteredBalances = <T extends GetFilteredBalancesData>({
  data = [],
  isSmallBalancesVisible = false,
  isDuplicatesVisible = true,
}: GetFilteredBalancesProps<T>) =>
  data.filter((balance) => {
    if (!isDuplicatesVisible && !!balance.isTotalDoubling) {
      return false;
    }

    if (!isSmallBalancesVisible) {
      if (!!balance.isTotalSmallBalance || !!balance.isComparisonSmallBalance) {
        return false;
      }
    }

    return true;
  });

/**
 * The user's token selection — a single, mode-independent rule: a token is
 * selected unless the user excluded it (`ignoreTokens`). This intentionally
 * does NOT depend on the small-balances toggle; visibility of small tokens is a
 * separate display concern handled by {@link getVisibleTokenColumns}. The
 * result drives which assets each account keeps (`selectedTokens`).
 */
export const getFilteredTokens = ({ tokens = [], ignoreTokens = [] }: GetFilteredTokensParams) =>
  tokens.filter((item) => !ignoreTokens.includes(item.token));

/**
 * The token columns to render: the user's selection, minus tokens that are
 * small across all accounts while "show small balances" is off. Selection state
 * (checkboxes) is unaffected — this only hides all-small columns from the grid.
 */
export const getVisibleTokenColumns = ({
  tokens = [],
  ignoreTokens = [],
  isSmallBalancesVisible = false,
}: GetVisibleTokenColumnsParams) => {
  const selected = getFilteredTokens({ tokens, ignoreTokens });
  return isSmallBalancesVisible ? selected : selected.filter((item) => !item.isSmallBalance);
};
